import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, NavLink } from 'react-router-dom';
import { LayoutDashboard, Calendar, Users, MessageSquare, Briefcase, Clock, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import Sidebar from './Sidebar';
import BillingNotice from './BillingNotice';
import MfaGraceNotice from './MfaGraceNotice';
import { NavIndexProvider } from './navIndex';
import Header from './Header';
import AIReceptionist from '../AIReceptionist';
import { useFeatures, useModules } from '../../context/FeatureContext';
import { useAuth } from '../../context/AuthContext';

import { hueFor, isSchedulingRoute, isStickyReady } from './routes';

// Pages appear straight away: a short fade in, and no exit animation to wait for between pages
// (the old exit + enter added about half a second to every navigation).
const pageVariants = {
  initial: { opacity: 0 },
  in:      { opacity: 1 },
};
const pageTransition = { duration: .15, ease: 'easeOut' };
const schedulingVariants = {
  initial: { opacity: 0, y: 10 },
  in:      { opacity: 1, y: 0 },
};
const schedulingTransition = { duration: .22, ease: [.4, 0, .2, 1] };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn, { passive: true });
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

// The phone's bottom menu shows only what this organisation has, using the same feature and module
// gates as the sidebar. It used to always offer Messages and Jobs, which bounced orgs without them
// back to the dashboard.
function useBottomNav() {
  const features = useFeatures() || {};
  const modules = useModules() || {};
  const { user } = useAuth() || {};
  const feat = (k) => features[k] !== false;
  const mod = (k) => modules[k] !== false;
  const isAdmin = ['admin', 'office_user', 'super_admin'].includes(user?.role);
  const items = [
    { label: 'Home',     path: '/',          icon: LayoutDashboard },
    { label: 'Schedule', path: '/scheduler', icon: Calendar },
    { label: 'Clients',  path: '/clients',   icon: Users },
  ];
  // Same gates as the sidebar (Sidebar.jsx: messagingEnabled, mod('job_board'), timesheets, incidents).
  const messaging = features.enable_general_chat || features.enable_team_chats || features.enable_direct_messaging;
  if (messaging) items.push({ label: 'Messages', path: '/messages', icon: MessageSquare });
  if (mod('job_board')) items.push({ label: 'Jobs', path: '/job-board', icon: Briefcase });
  const extras = [
    feat('enable_timesheets') && { label: 'Timesheets', path: '/timesheets', icon: Clock },
    isAdmin && features.enable_incidents && { label: 'Incidents', path: '/incidents', icon: AlertTriangle },
  ].filter(Boolean);
  for (const x of extras) { if (items.length < 5) items.push(x); }
  return items;
}

function MobileBottomNav() {
  const { pathname } = useLocation();
  const BOTTOM_NAV = useBottomNav();
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 md:hidden print:hidden bg-white/95 backdrop-blur-md border-t border-slate-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="flex">
        {BOTTOM_NAV.map(({ label, path, icon: Icon }) => {
          const active = path === '/' ? pathname === '/' : pathname.startsWith(path);
          return (
            <NavLink key={path} to={path}
              className={clsx(
                'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px]',
                'text-[11px] font-semibold transition-colors active:scale-95',
                active ? 'text-primary-600' : 'text-slate-500'
              )}>
              <Icon size={21} strokeWidth={active ? 2.2 : 1.8} />
              <span>{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

export default function Layout({ children }) {
  // Remember the sidebar collapse so data-heavy pages (Scheduler, reports) keep the
  // extra width once the user collapses it — instead of resetting to 240px every load.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const sw = collapsed ? 72 : 240;

  useEffect(() => { try { localStorage.setItem('sidebarCollapsed', collapsed ? '1' : '0'); } catch { /* ignore */ } }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  return (
    // The nav index lives above the sidebar so search, pins and recents survive route changes
    // instead of being rebuilt on every navigation.
    <NavIndexProvider>
    <div className={`flex min-h-screen ${isSchedulingRoute(pathname) ? 'bg-slate-50' : 'app-bg'}`}>
      {/* Mobile overlay backdrop */}
      <AnimatePresence>
        {isMobile && mobileOpen && (
          <motion.div
            className="fixed inset-0 bg-black/60 z-40 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
      />

      <motion.div
        animate={{ marginLeft: isMobile ? 0 : sw }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="flex-1 flex flex-col min-w-0 print:!ml-0"
      >
        <Header setMobileOpen={setMobileOpen} />
        <motion.main
          // Keyed by the record for a client's or staff member's pages, so moving between its tabs
          // keeps the record frame mounted (and doesn't replay the page fade) instead of remounting.
          key={(pathname.match(/^\/(clients|staff)\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) || [pathname])[0]}
          id="main"
          variants={isSchedulingRoute(pathname) ? schedulingVariants : pageVariants}
          initial="initial"
          animate="in"
          transition={isSchedulingRoute(pathname) ? schedulingTransition : pageTransition}
          // The document scrolls, not <main> (it grows to its content). `overflow-auto` made <main>
          // the scroll container for position: sticky while never scrolling itself, so every sticky
          // table header, toolbar and save bar scrolled away. Pages now clip sideways instead, so
          // sticky works; `app-root` gives them the shared form and table surface (index.css).
          // Scheduling routes keep their original frame.
          className={isSchedulingRoute(pathname)
            ? 'flex-1 p-4 md:p-6 flex flex-col pb-20 md:pb-6 overflow-auto'
            : `app-root hue-${hueFor(pathname)} flex-1 p-4 md:p-6 flex flex-col pb-20 md:pb-6 print:p-0 print:overflow-visible ${isStickyReady(pathname) ? 'overflow-x-clip' : 'overflow-auto'}`}
        >
          <BillingNotice />
          <MfaGraceNotice />
          {children}
        </motion.main>
      </motion.div>

      <MobileBottomNav />
      {/* The website receptionist chat is the PUBLIC intake bot ("I need support for my child").
          It used to float on every page for every org, so staff chatting to it created fake leads
          and it covered the phone's bottom menu. It now appears only on the AI receptionist page,
          as a way to try it out. */}
      {pathname.startsWith('/ai-receptionist') && <AIReceptionist />}
    </div>
    </NavIndexProvider>
  );
}
